"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHubAlertFeed = void 0;
const react_1 = require("react");
const useAlertChangeRefresh_1 = require("./useAlertChangeRefresh");
const queries_1 = require("../../graphql/queries");
const types_1 = require("../../types");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const POLLING_INTERVAL_MS = 30_000;
const FETCH_THROTTLE_MS = 5_000;
const DEFAULT_HISTORY_DAYS = 30;
const SECONDS_PER_DAY = 86_400;
/** One hub-wide query instead of the per-sensor fan-out the category hooks use. */
const ALERT_FETCH_LIMIT = 200;
/**
 * Whole-household alert feed for the Alerts screen: every alert row for the
 * hub (and any linked gateways), split into active and recently-resolved
 * buckets.
 *
 * Unlike `useActiveAlerts` (per-category, per-sensor fan-out) this uses the
 * single `getSensorAlertsByHub` query, so it also picks up alerts whose
 * `sensorId` is not a paired sensor — notably the hub-level types
 * (`HUB_OFFLINE` / `HUB_RADIO_FAULT` / legacy `HUB_INACTIVE`), where the
 * sensorId is the hub's own id. That is exactly why the gateway's id has to be
 * queried in its own right: a gateway going offline writes an alert keyed to
 * the GATEWAY, so it is invisible to a primary-hub-only query — the one alert
 * the household most needs to see.
 *
 * @param hubId - A single hub id, or the household scope set (primary hub plus
 * every linked gateway).
 */
const useHubAlertFeed = (hubId, options) => {
    const historyDays = options?.historyDays ?? DEFAULT_HISTORY_DAYS;
    const [rawAlerts, setRawAlerts] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const intervalRef = (0, react_1.useRef)(null);
    const lastFetchTime = (0, react_1.useRef)(0);
    // Collapsed to a primitive so the fetch depends on the hub SET, not on the
    // identity of an array the caller rebuilds every render.
    const hubIdKey = (Array.isArray(hubId) ? hubId : [hubId])
        .filter((id) => !!id)
        .filter((id, i, all) => all.indexOf(id) === i)
        .sort()
        .join(',');
    const fetchAlerts = (0, react_1.useCallback)(async (force = false) => {
        const hubIds = hubIdKey ? hubIdKey.split(',') : [];
        if (hubIds.length === 0) {
            setRawAlerts([]);
            setLoading(false);
            return;
        }
        const now = Date.now();
        if (!force && now - lastFetchTime.current < FETCH_THROTTLE_MS)
            return;
        lastFetchTime.current = now;
        try {
            // getSensorAlertsByHub returns a bare array — no nextToken in the
            // selection set, so there is nothing to page through here.
            // allSettled: an unreachable gateway must not blank the primary hub's
            // alert list (or its sidebar badge).
            const responses = await Promise.allSettled(hubIds.map((id) => amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorAlertsByHub,
                variables: { hubId: id, limit: ALERT_FETCH_LIMIT },
            })));
            const seen = new Set();
            const merged = [];
            let failures = 0;
            responses.forEach((result, index) => {
                if (result.status === 'rejected') {
                    failures += 1;
                    console.error(`[useHubAlertFeed] Failed to fetch alerts for hub ${hubIds[index]}:`, result.reason);
                    return;
                }
                const alerts = result.value?.data?.getSensorAlertsByHub || [];
                alerts.forEach((a) => {
                    if (seen.has(a.id))
                        return;
                    seen.add(a.id);
                    merged.push(a);
                });
            });
            // Every hub failed — keep whatever is on screen rather than flashing an
            // empty "no active alerts" state over a real outage.
            if (failures < hubIds.length) {
                setRawAlerts(merged);
            }
        }
        catch (error) {
            console.error('[useHubAlertFeed] Failed to fetch hub alerts:', error);
            // Keep existing
        }
        finally {
            setLoading(false);
        }
    }, [hubIdKey]);
    (0, react_1.useEffect)(() => {
        fetchAlerts();
        intervalRef.current = setInterval(() => fetchAlerts(), POLLING_INTERVAL_MS);
        return () => {
            if (intervalRef.current)
                clearInterval(intervalRef.current);
        };
    }, [fetchAlerts]);
    const refreshOnAlertChange = (0, react_1.useCallback)(() => {
        fetchAlerts(true);
    }, [fetchAlerts]);
    (0, useAlertChangeRefresh_1.useAlertChangeRefresh)(refreshOnAlertChange);
    const { activeAlerts, historyAlerts } = (0, react_1.useMemo)(() => {
        const active = [];
        const resolved = [];
        rawAlerts.forEach((alert) => {
            ((0, types_1.isAlertActive)(alert) ? active : resolved).push(alert);
        });
        // Timestamps are epoch SECONDS throughout the alert pipeline.
        const cutoff = Math.floor(Date.now() / 1000) - historyDays * SECONDS_PER_DAY;
        const historyTime = (alert) => alert.clearedAt ?? alert.timestamp ?? 0;
        return {
            activeAlerts: active.sort((a, b) => {
                // NaN when a severity is unrecognized — fall through to recency.
                const bySeverity = (0, types_1.sortAlertsBySeverityFunction)(a, b);
                if (bySeverity)
                    return bySeverity;
                return (b.timestamp ?? 0) - (a.timestamp ?? 0);
            }),
            historyAlerts: resolved
                .filter((alert) => historyTime(alert) >= cutoff)
                .sort((a, b) => historyTime(b) - historyTime(a)),
        };
    }, [rawAlerts, historyDays]);
    return {
        activeAlerts,
        historyAlerts,
        activeCount: activeAlerts.length,
        loading,
        refetch: refreshOnAlertChange,
    };
};
exports.useHubAlertFeed = useHubAlertFeed;
